/**
 * The fixed chrome around a layout's page content: the app bar and the
 * CAPCO classification banners. Both are `position: fixed`, so they take no
 * room in the flow — the layout pads its <main> by these sizes instead.
 *
 * A layout publishes the sizes as CSS custom properties on its root element,
 * and every page under it inherits them:
 *
 * - `--app-chrome-top`     px covered at the top (app bar + top banner)
 * - `--app-chrome-bottom`  px covered at the bottom (bottom banner)
 * - `--app-content-height` the viewport height left between them
 *
 * A full-height page sizes itself with `h-[var(--app-content-height)]`, and
 * a fixed or sticky element insets itself by the chrome vars, rather than
 * hard-coding the app bar (`100vh - 50px`) and forgetting the banners
 * (#2271).
 * `index.css` gives `:root` the no-banner app-layout values for anything
 * rendered outside a layout (portals, tests).
 */
import type { CSSProperties } from 'react';
import { CLASSIFICATION_BANNER_HEIGHT } from '@brew/ui';

/** The app bar's fixed height in px (NavigationBar's `h-[50px]`). */
export const NAVBAR_HEIGHT = 50;

export interface AppChrome {
  top: number;
  bottom: number;
}

export function appChrome({ navbar, capco }: { navbar: boolean; capco: boolean }): AppChrome {
  const banner = capco ? CLASSIFICATION_BANNER_HEIGHT : 0;
  return {
    top: (navbar ? NAVBAR_HEIGHT : 0) + banner,
    bottom: banner,
  };
}

/** The custom properties a layout sets on its root element. */
export function appChromeStyle({ top, bottom }: AppChrome): CSSProperties {
  return {
    '--app-chrome-top': `${top}px`,
    '--app-chrome-bottom': `${bottom}px`,
    '--app-content-height': `calc(100vh - ${top + bottom}px)`,
  } as CSSProperties;
}
